// Auto-extracted from TourContext.jsx to reduce monolith size.
// Source of truth for all guided-tour step definitions per route.

// ============== TOUR CONFIGURATIONS BY PAGE ==============
export const TOUR_CONFIGS = {
  dashboard: {
    name: "Dashboard",
    steps: [
      {
        id: 1,
        targetSelector: "[data-testid='sidebar']",
        title: "Your Navigation",
        description: "Dashboard for overview, Projects for all jobs, Crew for your team, Reports for analytics.",
        tip: "You'll mostly live in Dashboard and Projects day-to-day.",
        position: "right"
      },
      {
        id: 2,
        targetSelector: "[data-testid='stat-cards']",
        title: "Business Health at a Glance",
        description: "Pipeline = total open job value. Win Rate = how often you close. Active = jobs in progress. These update automatically.",
        tip: null,
        position: "bottom"
      },
      {
        id: 3,
        targetSelector: "[data-testid='pipeline-strip']",
        title: "The Project Pipeline",
        description: "Every job moves through 7 stages: Lead → Opportunity → Concept → Quote → Plans → Build → Complete. Numbers show how many jobs are at each stage.",
        tip: "Concept is where Angela generates renders — your secret weapon to win jobs.",
        position: "top"
      },
      {
        id: 4,
        targetSelector: "[data-testid='recent-projects-card']",
        title: "Recent Projects",
        description: "Your latest jobs. Progress bar shows how far along each is. Click any project to open it.",
        tip: "Green = complete, orange = in progress, purple = concept stage.",
        position: "top"
      },
      {
        id: 5,
        targetSelector: "[data-testid='angela-insight-card']",
        title: "Angela — Your AI Assistant",
        description: "Angela watches your projects and tells you what to do next. She drafts messages, builds quotes, and generates renders.",
        tip: "Angela is always purple. Use her every day.",
        position: "left"
      },
      {
        id: 6,
        targetSelector: "[data-testid='training-btn']",
        title: "Come Back Anytime",
        description: "Hit Training any time to re-run this tour or open a specific module. Share with new crew for onboarding.",
        tip: null,
        position: "bottom"
      }
    ]
  },
  projects: {
    name: "Projects",
    steps: [
      {
        id: 1,
        targetSelector: "[data-testid='projects-header']",
        title: "Your Project Hub",
        description: "This is where all your projects live. Track every job from first call to final invoice.",
        tip: "Use filters to quickly find specific projects.",
        position: "bottom"
      },
      {
        id: 2,
        targetSelector: "[data-testid='new-project-btn']",
        title: "Start a New Project",
        description: "Click here to create a new lead. Enter client details, project scope, and let Angela take it from there.",
        tip: "New leads automatically get assigned a project number.",
        position: "bottom"
      },
      {
        id: 3,
        targetSelector: "[data-testid='stage-filters']",
        title: "Filter by Stage",
        description: "Quickly see which projects are in each stage. Great for daily standups and pipeline reviews.",
        tip: "Lead-stage projects show status badges and overdue follow-up alerts.",
        position: "bottom"
      },
      {
        id: 4,
        targetSelector: "[data-testid='project-card']",
        title: "Project Cards",
        description: "Each card shows key info: project name, value, stage, and contact. Lead projects also show their lead status and last contact date.",
        tip: "The colored badge shows the current stage. Amber clock = overdue follow-up.",
        position: "right"
      },
      {
        id: 5,
        targetSelector: "[data-testid='search-projects']",
        title: "Search Projects",
        description: "Find any project by name, client, or ARQ number. Results update as you type.",
        tip: null,
        position: "bottom"
      }
    ]
  },
  projectDetail: {
    name: "Project Detail",
    steps: [
      {
        id: 1,
        targetSelector: "[data-testid='lifecycle-pipeline']",
        title: "Project Lifecycle",
        description: "Every project moves through 7 stages. The active stage is highlighted. Completed stages show a checkmark.",
        tip: "You can jump back to previous stages anytime.",
        position: "bottom"
      },
      {
        id: 2,
        targetSelector: "[data-testid='stage-content']",
        title: "Stage Content",
        description: "This area changes based on the active stage. Each stage has sub-steps — the Next button shows the name of the next sub-step.",
        tip: "Complete sub-steps sequentially. The last sub-step's Next button advances to the next pipeline stage.",
        position: "top"
      },
      {
        id: 3,
        targetSelector: "[data-testid='project-actions']",
        title: "Project Actions",
        description: "Client Contact, Message Client (SMS/Email with Angela Polish), Message Crew, Edit Project, and copy Client/Crew Portal links.",
        tip: "Use Message Client to send polished SMS or emails. Toggle the portal link on/off.",
        position: "right"
      },
      {
        id: 4,
        targetSelector: "[data-testid='sidebar-client-link']",
        title: "Client & Crew Portal Links",
        description: "One-click copy the Client Portal or Crew Portal link. Share via SMS, email, or any messaging app.",
        tip: "Clients can review concepts, approve quotes, and track build progress through the portal.",
        position: "left"
      }
    ]
  },
  leadStage: {
    name: "Lead Stage",
    steps: [
      {
        id: 1,
        targetSelector: "[data-testid='step-capture-content']",
        title: "Capture — Sub-step 1 of 3",
        description: "Capture the project context: square footage, project type, HOA notes, and decision-makers. This data drives downstream measurements and quoting.",
        tip: "Fill HOA notes early — restrictions can rule out materials and styles upfront.",
        position: "top",
        clickBefore: "[data-testid='step-capture']"
      },
      {
        id: 2,
        targetSelector: "[data-testid='step-vision-content']",
        title: "Vision — Sub-step 2 of 3",
        description: "Capture the client's project vision in their own words. This is the source-of-truth narrative Angela uses later to write the Vision Spec render prompt during Concept.",
        tip: "Quote the client directly. Style cues, materials, mood, and 'must-haves' make for stronger AI renders downstream.",
        position: "top",
        clickBefore: "[data-testid='step-vision']"
      },
      {
        id: 3,
        targetSelector: "[data-testid='polish-vision-btn']",
        title: "Polish Vision with Angela",
        description: "Type the client's raw words or rough notes, then click Polish Vision. Angela rewrites them into a clear, professional vision narrative while preserving every detail.",
        tip: "First click reads 'Draft Vision' (no text yet) and turns into 'Polish Vision' once you start typing. Re-run anytime to refine.",
        position: "left",
        clickBefore: "[data-testid='step-vision']"
      },
      {
        id: 4,
        targetSelector: "[data-testid='step-qualify-content']",
        title: "Qualify — Sub-step 3 of 3",
        description: "Set the opportunity value, assign a project manager, and review the qualification completeness before advancing the lead to Opportunity.",
        tip: "Fill every field to reach 100% — a complete profile makes downstream quoting and scheduling far smoother.",
        position: "top",
        clickBefore: "[data-testid='step-qualify']"
      }
    ]
  },
  conceptStage: {
    name: "Concept Stage",
    steps: [
      {
        id: 1,
        targetSelector: "[data-testid='step-vision-content']",
        title: "Vision Specs — Sub-step 1 of 2",
        description: "Two cards on this step: the Client Vision Statement (the client's raw words) and the Vision Spec — Angela's detailed render prompt synthesized from vision + measurements + requirements + reference analysis.",
        tip: "Vision Specs are auto-saved as you edit and used as the primary prompt when generating concepts.",
        position: "top",
        clickBefore: "[data-testid='step-vision']"
      },
      {
        id: 2,
        targetSelector: "[data-testid='generate-vision-spec-btn']",
        title: "Generate Vision Spec",
        description: "Click 'Generate Vision Spec' once you've captured the client vision and uploaded references. Angela weaves it all into a render-ready paragraph for FLUX.",
        tip: "Re-run anytime — the new spec replaces the old one. Edit freely afterwards; your edits auto-save.",
        position: "left",
        clickBefore: "[data-testid='step-vision']"
      },
      {
        id: 3,
        targetSelector: "[data-testid='step-concepts-content']",
        title: "Concept Gen — Sub-step 2 of 2",
        description: "Generate AI concept renders using the Vision Spec (or a manual prompt override). Toggle between AI-generated renders and manually uploaded reference images.",
        tip: "Generate 3 concepts to give clients options. Manual mode is great for showing Pinterest inspiration.",
        position: "top",
        clickBefore: "[data-testid='step-concepts']"
      },
      {
        id: 4,
        targetSelector: "[data-testid='toggle-feedback-panel']",
        title: "Client Feedback",
        description: "Collapsible panel below concepts. Share the Client Portal link — feedback appears here automatically. Non-blocking, so you can advance without waiting.",
        tip: "Once a client approves a concept in the portal, you can advance to Quote.",
        position: "bottom",
        clickBefore: "[data-testid='step-concepts']"
      }
    ]
  },
  quoteStage: {
    name: "Quote Stage",
    steps: [
      {
        id: 1,
        targetSelector: "[data-testid='step-overview-content']",
        title: "Overview — Sub-step 1 of 4",
        description: "Angela auto-drafts the quote scope from your line items, client vision, and approved concept. Review the narrative before clients see it.",
        tip: "Click 'Generate Overview' to refresh after major line-item changes.",
        position: "top",
        clickBefore: "[data-testid='step-overview']"
      },
      {
        id: 2,
        targetSelector: "[data-testid='step-items-content']",
        title: "Line Items — Sub-step 2 of 4",
        description: "Add every component: materials, labor, permits, equipment. Quantity × Unit Price feeds the internal Cost Subtotal. Auto-saves as you type.",
        tip: "Group similar items into sections for clarity. The line-item subtotal is hidden from the client when markup is on.",
        position: "top",
        clickBefore: "[data-testid='step-items']"
      },
      {
        id: 3,
        targetSelector: "[data-testid='step-margin-content']",
        title: "Margin — Sub-step 3 of 4",
        description: "Set Overhead % and Target Margin %. Together with line items they produce the Client Quote Price (= what the client pays) shown in the header and PDF.",
        tip: "Both sliders persist per project. Margin under 15% turns red — a warning that you're cutting too close.",
        position: "top",
        clickBefore: "[data-testid='step-margin']"
      },
      {
        id: 4,
        targetSelector: "[data-testid='step-terms-content']",
        title: "Terms — Sub-step 4 of 4",
        description: "Payment terms, validity period, optional client-facing notes. These flow into the PDF and the Client Portal Approve Quote screen.",
        tip: "Defaults come from Settings → Quote Defaults. Edit them per quote when needed.",
        position: "top",
        clickBefore: "[data-testid='step-terms']"
      }
    ]
  },
  opportunityStage: {
    name: "Opportunity Stage",
    steps: [
      {
        id: 1,
        targetSelector: "[data-testid='step-measurements-content']",
        title: "Measurements — Sub-step 1 of 3",
        description: "Record site dimensions area by area (length, width, depth). These measurements feed Angela's Vision Spec and downstream materials math.",
        tip: "Use the inline edit pencil to update any row. Measurements display as concise chips on the Concept stage.",
        position: "top",
        clickBefore: "[data-testid='step-measurements']"
      },
      {
        id: 2,
        targetSelector: "[data-testid='step-photos-content']",
        title: "Photos — Sub-step 2 of 3",
        description: "Upload site photos and reference / inspiration images. Reference photos are analyzed by Angela to identify materials, style, and key features for the Vision Spec.",
        tip: "Mark photos as 'site' vs 'reference' — Angela treats them differently in the render pipeline.",
        position: "top",
        clickBefore: "[data-testid='step-photos']"
      },
      {
        id: 3,
        targetSelector: "[data-testid='step-notes-content']",
        title: "Requirements — Sub-step 3 of 3",
        description: "List must-haves, nice-to-haves, and exclusions with priority. These constraints are honored by Angela during Vision Spec generation and surface verbatim on the client quote.",
        tip: "High-priority exclusions are critical — Angela explicitly avoids them in renders.",
        position: "top",
        clickBefore: "[data-testid='step-notes']"
      }
    ]
  },
  plansStage: {
    name: "Plans Stage",
    steps: [
      {
        id: 1,
        targetSelector: "[data-testid='step-spec-content']",
        title: "Specification — Sub-step 1 of 4",
        description: "Angela synthesizes the project specification — full scope of work, features, and exclusions — from your quote and approved concept. Edit freely; debounced auto-save.",
        tip: "Click 'Regenerate Spec' to rebuild after material changes.",
        position: "top",
        clickBefore: "[data-testid='step-spec']"
      },
      {
        id: 2,
        targetSelector: "[data-testid='step-materials-content']",
        title: "Materials — Sub-step 2 of 4",
        description: "Final material list with quantities, units, and per-item costs. This list drives the Build stage Material Approval queue.",
        tip: "Crew sees these materials once Build starts — keep names client-friendly.",
        position: "top",
        clickBefore: "[data-testid='step-materials']"
      },
      {
        id: 3,
        targetSelector: "[data-testid='step-tasks-content']",
        title: "Tasks — Sub-step 3 of 4",
        description: "Construction tasks with planned dates and crew assignments. These tasks become the Build Board cards.",
        tip: "Sequence tasks deliberately — they show up in the same order on the Crew Portal.",
        position: "top",
        clickBefore: "[data-testid='step-tasks']"
      },
      {
        id: 4,
        targetSelector: "[data-testid='step-documents-content']",
        title: "Documents — Sub-step 4 of 4",
        description: "Upload permits, site plans, surveys, and any client-supplied docs. All documents are visible on the Client Portal once approved.",
        tip: "Drag-and-drop multiple files at once. PDF and image formats are supported.",
        position: "top",
        clickBefore: "[data-testid='step-documents']"
      }
    ]
  },
  buildStage: {
    name: "Build Stage",
    steps: [
      {
        id: 1,
        targetSelector: "[data-testid='material-approval-step']",
        title: "Material Approval — Sub-step 1 of 2",
        description: "Review each material line and approve or deny. Crew sees approval status in real-time on the Crew Portal — they don't pick up materials until approved.",
        tip: "Click the green check to approve, red X to deny. Denials prompt for a reason that's relayed to the crew.",
        position: "top",
        clickBefore: "[data-testid='build-step-materials']"
      },
      {
        id: 2,
        targetSelector: "[data-testid='build-column-todo']",
        title: "Build Board — Sub-step 2 of 2",
        description: "Kanban board for construction tasks. Drag cards between To Do, In Progress, and Done. Crew can upload progress photos and notes directly from the Crew Portal.",
        tip: "Status changes here trigger client-facing milestone notifications.",
        position: "top",
        clickBefore: "[data-testid='build-step-board']"
      }
    ]
  },
  completeStage: {
    name: "Complete Stage",
    steps: [
      {
        id: 1,
        targetSelector: "[data-testid='step-walkthrough-content']",
        title: "Walkthrough — Sub-step 1 of 2",
        description: "Run the final walkthrough checklist with the client. Capture any punch-list items and confirm sign-off on each completed deliverable.",
        tip: "Punch-list items roll back to the Build Board until resolved.",
        position: "top",
        clickBefore: "[data-testid='step-walkthrough']"
      },
      {
        id: 2,
        targetSelector: "[data-testid='step-handover-content']",
        title: "Handover — Sub-step 2 of 2",
        description: "Generate the final handover package: warranty docs, care instructions, before/after photos, and a thank-you message. Sent automatically to the Client Portal.",
        tip: "Encourage clients to leave a review here — testimonials drive new leads.",
        position: "top",
        clickBefore: "[data-testid='step-handover']"
      }
    ]
  },
  clientMessenger: {
    name: "Client Messenger",
    steps: [
      {
        id: 1,
        targetSelector: "[data-testid='messenger-recipient-name']",
        title: "Client Recipient",
        description: "Shows the client who will receive your message. Contact info is pulled from the project.",
        tip: "Update client info from the sidebar's Client Contact action.",
        position: "bottom"
      },
      {
        id: 2,
        targetSelector: "[data-testid='messenger-tab-sms'], [data-testid='messenger-tab-email']",
        title: "SMS or Email",
        description: "Choose how to reach your client. SMS for quick updates, Email for detailed messages.",
        tip: "SMS messages over 160 chars send as MMS automatically.",
        position: "bottom"
      },
      {
        id: 3,
        targetSelector: "[data-testid='toggle-portal-link']",
        title: "Attach Portal Link (Optional)",
        description: "Toggle to include the Client Portal link in your message. Leave off for a plain custom message.",
        tip: "The portal link lets clients approve concepts and sign quotes.",
        position: "bottom"
      },
      {
        id: 4,
        targetSelector: "[data-testid='angela-draft-btn']",
        title: "Angela Drafts for You",
        description: "Let Angela write a professional message. She knows the project context and personalizes every draft.",
        tip: "You can always edit Angela's draft before sending.",
        position: "left"
      },
      {
        id: 5,
        targetSelector: "[data-testid='angela-polish-btn']",
        title: "Polish Your Message",
        description: "Type a rough message, then hit Polish. Angela rewrites it professionally while keeping your meaning and any links.",
        tip: "Polish works on both SMS and email. Great for quick, polished communication.",
        position: "left"
      },
      {
        id: 6,
        targetSelector: "[data-testid='messenger-send-btn']",
        title: "Send It",
        description: "Review the message and hit Send. The client gets an SMS or email.",
        tip: null,
        position: "top"
      }
    ]
  },
  crewMessenger: {
    name: "Crew Messenger",
    steps: [
      {
        id: 1,
        targetSelector: "[data-testid='assign-crew-btn']",
        title: "Assign Crew",
        description: "Manage which crew members are assigned to this project. Only assigned members appear as recipients.",
        tip: "Assign crew early so they can access the Crew Portal.",
        position: "left"
      },
      {
        id: 2,
        targetSelector: "[data-testid='crew-tab-sms'], [data-testid='crew-tab-email']",
        title: "SMS or Email",
        description: "Choose the channel. SMS for quick field updates, Email for detailed instructions.",
        tip: "SMS messages over 160 chars send as MMS automatically.",
        position: "bottom"
      },
      {
        id: 3,
        targetSelector: "[data-testid='toggle-crew-portal-link']",
        title: "Attach Crew Portal Link",
        description: "Toggle to include the Crew Portal link. Leave off for a plain text message.",
        tip: "The portal link gives crew on-site access to project specs, materials, and tasks.",
        position: "bottom"
      },
      {
        id: 4,
        targetSelector: "[data-testid='crew-draft-btn']",
        title: "Angela Drafts for You",
        description: "Angela generates a professional message tailored to your crew. She knows the project context.",
        tip: "Edit the draft to add specific instructions for the field.",
        position: "left"
      },
      {
        id: 5,
        targetSelector: "[data-testid='crew-polish-btn']",
        title: "Polish Your Message",
        description: "Type a rough message, then hit Polish. Angela cleans it up professionally.",
        tip: "Perfect for turning quick notes into professional crew communications.",
        position: "left"
      },
      {
        id: 6,
        targetSelector: "[data-testid='crew-send-btn']",
        title: "Send to Crew",
        description: "Review and send. Selected crew members receive the message via SMS or email.",
        tip: null,
        position: "top"
      }
    ]
  },
  crew: {
    name: "Crew",
    steps: [
      {
        id: 1,
        targetSelector: "[data-testid='crew-header']",
        title: "Your Crew",
        description: "Manage your team members here. Add new crew, assign roles, and control access levels.",
        tip: "Each crew member gets their own portal access.",
        position: "bottom"
      },
      {
        id: 2,
        targetSelector: "[data-testid='add-crew-btn']",
        title: "Add New Crew",
        description: "Add team members with their name, role, phone, and email. Roles include Project Director, Foreman, Laborers, and trade specialists.",
        tip: "Phone numbers are used for SMS messaging from the Crew Messenger.",
        position: "left"
      },
      {
        id: 3,
        targetSelector: "[data-testid='crew-roles']",
        title: "Filter by Trade",
        description: "Filter your crew by role: Project Director, Foreman, Laborers, Carpenter, Electrician, Plumber, and more.",
        tip: "Click a trade filter to see only those team members.",
        position: "bottom"
      },
      {
        id: 4,
        targetSelector: "[data-testid='crew-list']",
        title: "Crew List",
        description: "See all team members, their roles, and contact info. Click any member to edit their details.",
        tip: "Inactive members can be archived, not deleted.",
        position: "top"
      }
    ]
  },
  reports: {
    name: "Reports",
    steps: [
      {
        id: 1,
        targetSelector: "[data-testid='reports-page'] > div:first-child",
        title: "Business Reports",
        description: "Track your business performance with real-time analytics. Revenue, pipeline, win rates, and lead sources.",
        tip: "Check reports weekly to spot trends early.",
        position: "bottom"
      },
      {
        id: 2,
        targetSelector: "[data-testid='time-range-filter']",
        title: "Date Range",
        description: "Filter by preset ranges (7 Days, 30 Days, etc.) or choose Custom with a start and end date picker.",
        tip: "Use 'Custom' to analyze a specific project period.",
        position: "bottom"
      },
      {
        id: 3,
        targetSelector: "[data-testid='revenue-chart']",
        title: "Revenue Chart",
        description: "See your revenue trends over time. Hover over points for exact values.",
        tip: "Look for seasonal patterns in your business.",
        position: "bottom"
      },
      {
        id: 4,
        targetSelector: "[data-testid='lead-source-chart']",
        title: "Leads by Source",
        description: "Donut chart showing where your leads come from: Referral, Google, Instagram, Walk-in, or Other. Always shows all 5 sources.",
        tip: "Invest more in the channels bringing the most leads.",
        position: "left"
      },
      {
        id: 5,
        targetSelector: "[data-testid='stages-chart']",
        title: "Projects by Stage",
        description: "Bar chart of projects at each pipeline stage. Identify bottlenecks in your sales process.",
        tip: "If Concept stage is crowded, focus on generating renders.",
        position: "left"
      },
      {
        id: 6,
        targetSelector: "[data-testid='stage-breakdown']",
        title: "Stage Value Breakdown",
        description: "Dollar value and count of projects at each stage. Colored bars show relative value.",
        tip: "Focus on stages with high value to maximize revenue.",
        position: "top"
      }
    ]
  },
  settings: {
    name: "Settings",
    steps: [
      {
        id: 1,
        targetSelector: "[data-testid='settings-page'] > div:first-child",
        title: "Account Settings",
        description: "Configure your account, company details, integrations, and preferences.",
        tip: "Keep your company info updated — it appears on client documents.",
        position: "bottom"
      },
      {
        id: 2,
        targetSelector: "[data-testid='company-section']",
        title: "Company Profile",
        description: "Your company name, logo, and contact info. This appears on quotes and client portals.",
        tip: "Upload a high-res logo for best results.",
        position: "right"
      },
      {
        id: 3,
        targetSelector: "[data-testid='subscription-section']",
        title: "Subscription & Billing",
        description: "Manage your Arq+ subscription, view usage, and upgrade your plan.",
        tip: "Upgrade to unlock more AI renders and team seats.",
        position: "right"
      },
      {
        id: 4,
        targetSelector: "[data-testid='team-section']",
        title: "Team Management",
        description: "Invite team members, manage roles, and control access permissions.",
        tip: "Each seat counts toward your subscription limit.",
        position: "right"
      }
    ]
  }
};

// Training modules for the module drawer
export const TRAINING_MODULES = [
  { id: "dashboard", icon: "LayoutDashboard", name: "Dashboard Overview", description: "Learn the main dashboard", steps: 6 },
  { id: "projects", icon: "FolderKanban", name: "Managing Projects", description: "Create and track projects", steps: 5 },
  { id: "projectDetail", icon: "FileText", name: "Project Details", description: "Navigate project stages", steps: 4 },
  { id: "leadStage", icon: "Target", name: "Lead Stage", description: "Capture, Vision & Qualify", steps: 4 },
  { id: "opportunityStage", icon: "Ruler", name: "Opportunity Stage", description: "Measurements, photos & requirements", steps: 3 },
  { id: "conceptStage", icon: "Sparkles", name: "Concept Stage", description: "Vision Specs & AI concept renders", steps: 4 },
  { id: "quoteStage", icon: "Calculator", name: "Quote Stage", description: "Overview, line items, margin & terms", steps: 4 },
  { id: "plansStage", icon: "ClipboardList", name: "Plans Stage", description: "Spec, materials, tasks & documents", steps: 4 },
  { id: "buildStage", icon: "Hammer", name: "Build Stage", description: "Material approval & build board", steps: 2 },
  { id: "completeStage", icon: "CheckCircle", name: "Complete Stage", description: "Walkthrough & handover", steps: 2 },
  { id: "clientMessenger", icon: "MessageSquare", name: "Client Messenger", description: "SMS/Email with Polish", steps: 6 },
  { id: "crewMessenger", icon: "Users", name: "Crew Messenger", description: "Message your crew", steps: 6 },
  { id: "crew", icon: "Users", name: "Team Management", description: "Manage your crew", steps: 4 },
  { id: "reports", icon: "BarChart3", name: "Reports & Analytics", description: "Revenue, leads & metrics", steps: 6 },
  { id: "settings", icon: "Settings", name: "Settings", description: "Configure your account", steps: 4 }
];

// Get tour config based on current route
export const getTourConfigForRoute = (pathname) => {
  if (pathname === "/" || pathname === "/dashboard") return "dashboard";
  if (pathname === "/projects") return "projects";
  if (pathname.match(/^\/projects\/[^/]+\/messenger$/)) return "clientMessenger";
  if (pathname.match(/^\/projects\/[^/]+\/crew$/)) return "crewMessenger";
  if (pathname.startsWith("/projects/")) return "projectDetail";
  if (pathname === "/crew") return "crew";
  if (pathname === "/reports" || pathname === "/crm/reports") return "reports";
  if (pathname === "/settings") return "settings";
  return "dashboard"; // Default
};

